import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Container } from "../../components/ui/Container";
import { Stack } from "../../components/ui/Stack";
import { Heading } from "../../components/ui/Heading";
import { Card } from "../../components/ui/Card";
import { Button } from "../../components/ui/Button";
import { Input } from "../../components/ui/Input";
import { Spinner } from "../../components/ui/Spinner";
import { Alert } from "../../components/ui/Alert";
import { useApi } from "../../context/ApiContext";

interface Payment {
 _id: string;
 order: {
  _id: string;
  orderNumber: string;
  email: string;
 };
 amount: number;
 currency: string;
 status: "pending" | "processing" | "completed" | "failed" | "refunded";
 method: "stripe" | "paypal" | "other";
 transactionId?: string;
 refundedAmount?: number;
 refundedAt?: string;
 failureReason?: string;
 createdAt: string;
}

export const Payments: React.FC = () => {
 const { t } = useTranslation();
 const { api } = useApi();
 const [payments, setPayments] = useState<Payment[]>([]);
 const [loading, setLoading] = useState(true);
 const [error, setError] = useState<string | null>(null);
 const [filterStatus, setFilterStatus] = useState("");
 const [filterMethod, setFilterMethod] = useState("");
 const [page, setPage] = useState(1);
 const [total, setTotal] = useState(0);
 const [limit] = useState(20);
 const [selectedPayment, setSelectedPayment] = useState<Payment | null>(null);
 const [refundAmount, setRefundAmount] = useState("");
 const [refundReason, setRefundReason] = useState("");
 const [processingRefund, setProcessingRefund] = useState(false);

 useEffect(() => {
  fetchPayments();
 }, [page, filterStatus, filterMethod]);

 const fetchPayments = async () => {
  try {
   setLoading(true);
   setError(null);
   const params = new URLSearchParams({
    page: page.toString(),
    limit: limit.toString(),
    ...(filterStatus && { status: filterStatus }),
    ...(filterMethod && { method: filterMethod }),
   });
   const response = await api.get(`/payments/admin?${params.toString()}`);
   setPayments(response.data.payments || []);
   setTotal(response.data.pagination?.total || 0);
  } catch (err: any) {
   setError(err.response?.data?.error || t("admin.payments.errors.fetch"));
  } finally {
   setLoading(false);
  }
 };

 const handleRefund = async () => {
  if (!selectedPayment) return;
  
  if (!window.confirm(t("admin.payments.confirmRefund", { 
   amount: `${selectedPayment.currency}${refundAmount || selectedPayment.amount}` 
  }))) {
   return;
  }

  try {
   setProcessingRefund(true);
   await api.post(`/payments/admin/${selectedPayment._id}/refund`, {
    amount: refundAmount ? parseFloat(refundAmount) : undefined,
    reason: refundReason || "Admin refund",
   });
   setSelectedPayment(null);
   setRefundAmount("");
   setRefundReason("");
   fetchPayments();
  } catch (err: any) {
   setError(err.response?.data?.error || t("admin.payments.errors.processRefund"));
  } finally {
   setProcessingRefund(false);
  }
 };

 const getStatusColor = (status: string) => {
  switch (status) {
   case "completed":
    return "bg-green-100 text-green-800";
   case "authorized":
    return "bg-blue-100 text-blue-800";
   case "pending":
   case "processing":
    return "bg-yellow-100 text-yellow-800";
   case "failed":
    return "bg-red-100 text-red-800";
   case "refunded":
    return "bg-purple-100 text-purple-800";
   default:
    return "bg-gray-100 text-gray-800";
  }
 };

 const totalPages = Math.ceil(total / limit);

 if (loading && payments.length === 0) {
  return (
   <Container>
    <Stack align="center" spacing="lg">
     <Spinner size="lg" />
    </Stack>
   </Container>
  );
 }

 return (
  <Container>
   <Stack spacing="lg">
    <Heading size="xl">{t("admin.payments.title")}</Heading>

    {error && <Alert variant="error">{error}</Alert>}

    <Card padding="lg">
     <Stack spacing="md">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
       <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
         {t("admin.payments.filterByStatus")}
        </label>
        <select
         value={filterStatus}
         onChange={(e) => {
          setFilterStatus(e.target.value);
          setPage(1);
         }}
         className="w-full px-3 py-2 border border-gray-300 "
        >
         <option value="">{t("admin.payments.allStatuses")}</option>
         <option value="pending">{t("orders.pending")}</option>
         <option value="processing">{t("admin.payments.processing")}</option>
         <option value="completed">{t("admin.payments.completed")}</option>
         <option value="failed">{t("admin.payments.failed")}</option>
         <option value="refunded">{t("admin.payments.refunded")}</option>
        </select>
       </div>
       <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
         {t("admin.payments.filterByMethod")}
        </label>
        <select
         value={filterMethod}
         onChange={(e) => {
          setFilterMethod(e.target.value);
          setPage(1);
         }}
         className="w-full px-3 py-2 border border-gray-300 "
        >
         <option value="">{t("admin.payments.allMethods")}</option>
         <option value="stripe">Stripe</option>
         <option value="paypal">PayPal</option>
         <option value="other">{t("admin.payments.other")}</option>
        </select>
       </div>
      </div>

      <div className="overflow-x-auto">
       <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
         <tr>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
           {t("admin.orders.title")}
          </th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
           {t("admin.payouts.amount")}
          </th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
           {t("admin.payouts.method")}
          </th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
           {t("admin.status")}
          </th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
           {t("admin.payments.transactionId")}
          </th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
           {t("admin.payments.date")}
          </th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
           {t("admin.actions")}
          </th>
         </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
         {payments.map((payment) => (
          <tr key={payment._id}>
           <td className="px-6 py-4 whitespace-nowrap">
            <div className="text-sm font-medium text-gray-900">
             {payment.order?.orderNumber || "N/A"}
            </div>
            <div className="text-xs text-gray-500">
             {payment.order?.email || ""}
            </div>
           </td>
           <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
            {payment.currency || "$"}{payment.amount.toFixed(2)}
            {payment.refundedAmount && (
             <div className="text-xs text-red-600">
              {t("admin.payments.refunded")}: {payment.currency || "$"}{payment.refundedAmount.toFixed(2)}
             </div>
            )}
           </td>
           <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
            {t(`admin.orders.paymentMethod.${payment.method}`) || payment.method}
           </td>
           <td className="px-6 py-4 whitespace-nowrap">
            <span className={`px-2 py-1 text-xs font-semibold ${getStatusColor(payment.status)}`}>
             {t(`admin.orders.paymentStatus.${payment.status}`) || payment.status}
            </span>
           </td>
           <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
            {payment.transactionId || "N/A"}
           </td>
           <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
            {new Date(payment.createdAt).toLocaleDateString()}
           </td>
           <td className="px-6 py-4 whitespace-nowrap text-sm">
            {payment.status === "completed" && !payment.refundedAmount && (
             <Button
              variant="outline"
              size="sm"
              onClick={() => setSelectedPayment(payment)}
             >
              {t("admin.payments.refund")}
             </Button>
            )}
           </td>
          </tr>
         ))}
        </tbody>
       </table>
      </div>

      {totalPages > 1 && (
       <div className="flex items-center justify-between">
        <div className="text-sm text-gray-500">
         {t("admin.payments.showing", { 
          start: ((page - 1) * limit) + 1, 
          end: Math.min(page * limit, total), 
          total 
         })}
        </div>
        <div className="flex gap-2">
         <Button
          variant="outline"
          disabled={page === 1}
          onClick={() => setPage(page - 1)}
         >
          {t("common.previous")}
         </Button>
         <Button
          variant="outline"
          disabled={page === totalPages}
          onClick={() => setPage(page + 1)}
         >
          {t("common.next")}
         </Button>
        </div>
       </div>
      )}
     </Stack>
    </Card>

    {selectedPayment && (
     <Card padding="lg">
      <Stack spacing="md">
       <Heading size="lg">{t("admin.payments.processRefund")}</Heading>
       <div className="space-y-4">
        <div>
         <label className="block text-sm font-medium text-gray-700 mb-2">
          {t("admin.payments.refundAmount")}
         </label>
         <Input
          type="number"
          step="0.01"
          min="0"
          max={selectedPayment.amount}
          value={refundAmount}
          onChange={(e) => setRefundAmount(e.target.value)}
          placeholder={t("admin.payments.refundAmountPlaceholder", { 
           max: `${selectedPayment.currency}${selectedPayment.amount.toFixed(2)}` 
          })}
         />
        </div>
        <div>
         <label className="block text-sm font-medium text-gray-700 mb-2">
          {t("admin.payments.reason")}
         </label>
         <select
          value={refundReason}
          onChange={(e) => setRefundReason(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 "
         >
          <option value="">{t("admin.payments.selectReason")}</option>
          <option value="Customer Request">{t("admin.payments.reasonCustomerRequest")}</option>
          <option value="Product Defect">{t("admin.payments.reasonProductDefect")}</option>
          <option value="Wrong Item Shipped">{t("admin.payments.reasonWrongItem")}</option>
          <option value="Item Not Received">{t("admin.payments.reasonNotReceived")}</option>
          <option value="Duplicate Payment">{t("admin.payments.reasonDuplicate")}</option>
          <option value="Cancelled Order">{t("admin.payments.reasonCancelled")}</option>
          <option value="Return Request">{t("admin.payments.reasonReturn")}</option>
          <option value="Other">{t("admin.payments.other")}</option>
         </select>
        </div>
        <div className="flex gap-2">
         <Button onClick={handleRefund} loading={processingRefund}>
          {t("admin.payments.processRefund")}
         </Button>
         <Button variant="outline" onClick={() => {
          setSelectedPayment(null);
          setRefundAmount("");
          setRefundReason("");
         }}>
          {t("common.cancel")}
         </Button>
        </div>
       </div>
      </Stack>
     </Card>
    )}
   </Stack>
  </Container>
 );
};

export default Payments;

